import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { api } from '../lib/api';
import type { Garden, GardenMember, User } from '../types';

interface AuthCtx {
  user: User | null;
  garden: Garden | null;
  members: GardenMember[];
  loading: boolean;
  register: (email: string, password: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  createGarden: (name: string) => Promise<void>;
  joinGarden: (code: string) => Promise<void>;
  refreshGarden: () => Promise<void>;
}

const Ctx = createContext<AuthCtx | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [garden, setGarden] = useState<Garden | null>(null);
  const [members, setMembers] = useState<GardenMember[]>([]);
  const [loading, setLoading] = useState(true);

  const loadGarden = useCallback(async () => {
    try {
      const res = await api.getGarden();
      setGarden(res.garden);
      setMembers(res.members);
    } catch {
      // no-op
    }
  }, []);

  useEffect(() => {
    const tok = localStorage.getItem('gm_token');
    if (!tok) { setLoading(false); return; }
    api.me()
      .then(async (u) => {
        setUser(u);
        if (u.garden_id) await loadGarden();
      })
      .catch(() => localStorage.removeItem('gm_token'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const register = useCallback(async (email: string, password: string) => {
    const res = await api.register(email, password);
    localStorage.setItem('gm_token', res.token);
    setUser(res.user);
    setGarden(null);
    setMembers([]);
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    const res = await api.login(email, password);
    localStorage.setItem('gm_token', res.token);
    setUser(res.user);
    if (res.user.garden_id) {
      await loadGarden();
    } else {
      setGarden(null);
      setMembers([]);
    }
  }, [loadGarden]);

  const logout = useCallback(() => {
    localStorage.removeItem('gm_token');
    setUser(null);
    setGarden(null);
    setMembers([]);
  }, []);

  const createGarden = useCallback(async (name: string) => {
    const g = await api.createGarden(name);
    setGarden(g);
    setMembers([]);
    // Refresh user so garden_id is updated
    const u = await api.me();
    setUser(u);
  }, []);

  const joinGarden = useCallback(async (code: string) => {
    await api.joinGarden(code);
    const u = await api.me();
    setUser(u);
    await loadGarden();
  }, [loadGarden]);

  const refreshGarden = useCallback(async () => {
    await loadGarden();
  }, [loadGarden]);

  return (
    <Ctx.Provider value={{ user, garden, members, loading, register, login, logout, createGarden, joinGarden, refreshGarden }}>
      {children}
    </Ctx.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
